import {EventEmitter} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {StorageUtil} from './storage-util';
import {CacheKeys} from '../enums/cache-keys.enum';
import {DomSanitizer} from '@angular/platform-browser';
import {Color} from '../enums/color';

type ElementType = 'rect' | 'path' | 'circle' | 'g';
type Position = 'right' | 'left' | 'top' | 'bottom';
type TextAnchor = 'start' | 'end' | 'middle';

export class SVGManagerUtil {
  private readonly svgNS = 'http://www.w3.org/2000/svg';
  private readonly unitClass = 'unit';
  private readonly clickableUnitClass = 'clickable-unit';
  private readonly ICON_TARGET = 'icon';
  private readonly TEXT_TARGET = 'text';
  private readonly MAIN_SYSTEM = 'mainSystem';
  public resultEmitter: EventEmitter<any> = new EventEmitter();
  private svgContainer: HTMLElement;
  private readonly colorRules: { [key: string]: any };
  private readonly clickHandlers: { [key: string]: (id: string) => any };
  private readonly svgIndex: any = ''; // Maps specific group IDs to their corresponding index
  private readonly sortedSvgIndex: any = ''; // Sorted maps specific group IDs to their corresponding index
  private readonly isModificationEnabled: boolean = false; // Indicates if SVG modification is enabled
  private readonly isClickEnabled: boolean = false; // Indicates if click handling is enabled
  private svgData: any = ''; // Data related to the SVG for conditional logic
  private activeTab: string = ''; // The currently active tab for applying specific rules

  constructor(
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
    colorRules: { [key: string]: any },
    clickHandlers: { [key: string]: (id: string) => any },
    svgIndex: any = '',
    isModificationEnabled: boolean = false,
    isClickEnabled: boolean = false,
  ) {
    this.colorRules = colorRules;
    this.clickHandlers = clickHandlers;
    this.svgIndex = svgIndex;
    this.sortedSvgIndex = Object
      .keys(this.svgIndex)
      .sort((a, b) => b.length - a.length);
    this.isModificationEnabled = isModificationEnabled;
    this.isClickEnabled = isClickEnabled;
  }

  /**
   * Loads the SVG from a provided path and modifies it according to the current configuration.
   * @param svgContainer - Container for SVG file
   * @param svgPath - Path to the SVG file.
   * @param svgData - Data related to the SVG that may influence modifications.
   * @param activeTab - The currently active tab used to apply specific color and click handlers.
   * @returns The selected result.
   */
  public loadAndModifySVG(svgContainer: HTMLElement, svgPath: string, svgData: any = '', activeTab: string = '') {
    if (!svgContainer) return;
    this.svgContainer = svgContainer;

    if (svgData !== '') {
      this.svgData = svgData;
    }

    if (activeTab !== '') {
      this.activeTab = activeTab;
    }

    fetch(svgPath)
      .then(response => response.text())
      .then(data => {
        this.handleSVGData(data);
      })
      .catch(error => {
        console.error('An error occurred while loading the SVG: ', error);
      });
  }

  /**
   * Handles the loaded SVG data and applies modifications based on the current configuration.
   * @param data - The raw SVG data to be inserted and processed.
   */
  private handleSVGData(data: string) {
    // If the svg container is found, load the SVG and modify it.
    if (this.svgContainer) {
      this.svgContainer.innerHTML = data;

      // Modify 'rect', 'path', and 'g' elements based on rules and handle clicks.
      this.setupElements('rect', this.handleModifyRect.bind(this), this.handleRectClick.bind(this)); // Modify 'rect' elements in the SVG
      this.setupElements('path', this.handleModifyPath.bind(this), this.handlePathClick.bind(this)); // Modify 'path' elements in the SVG
      this.setupElements('circle', this.handleModifyCircle.bind(this), this.handleCircleClick.bind(this)); // Modify 'circle' elements in the SVG
      this.setupElements('g', this.handleModifyGroup.bind(this), this.handleGroupClick.bind(this)); // Modify 'g' elements in the SVG
      this.setupIcon(this.activeTab); // Add icon to certain elements based on rules.
      this.setupText(this.activeTab); // Add dynamic text to certain elements based on rules.
      this.setUpMainSystemUnit(); // Modify element in mainSystem
    }
  }

  /**
   * Generic setup method for handling different types of SVG elements.
   * It applies modifications and attaches click handlers if enabled.
   * @param elementType - The SVG element type (e.g., 'rect', 'path', 'g').
   * @param modifyHandler - A function to modify the element (color, attributes, etc.).
   * @param clickHandler - A function to handle click events on the element.
   */
  private setupElements(
    elementType: ElementType,
    modifyHandler: (element: SVGGraphicsElement) => void,
    clickHandler: (element: SVGGraphicsElement) => any
  ) {
    const elements = this.svgContainer.querySelectorAll(elementType);

    elements.forEach(element => {
      // Only modify elements with the class 'unit'
      if (this.isModificationEnabled && element.classList.contains(this.unitClass)) {
        modifyHandler(element); // Apply element-specific modifications.
      }

      if (this.isClickEnabled && element.classList.contains(this.clickableUnitClass)) {
        element.style.cursor = 'pointer'; // Change cursor to pointer if clickable.
        element.addEventListener('click', () => {
          // Holds the result after an SVG element is clicked
          const selectedResult = clickHandler(element); // Execute the click handler.
          if (selectedResult) {
            this.resultEmitter.emit(selectedResult);
          }
        });
      }
    });
  }

  /**
   * Modifies the color of rect elements according to the rules.
   * @param rect - The SVG rect element to modify.
   */
  private handleModifyRect(rect: SVGRectElement) {
    this.applyColorModification(rect, this.activeTab);
  }

  /**
   * Modifies the color of path elements according to the rules.
   * @param path - The SVG path element to modify.
   */
  private handleModifyPath(path: SVGPathElement) {
    this.applyColorModification(path, this.activeTab);
  }

  /**
   * Modifies the color of circle elements according to the rules.
   * @param circle - The SVG circle element to modify.
   */
  private handleModifyCircle(circle: SVGCircleElement) {
    this.applyColorModification(circle, this.activeTab);
  }

  /**
   * Modifies the color of group elements ('g') according to the rules.
   * @param g - The SVG group element to modify.
   */
  private handleModifyGroup(g: SVGGElement) {
    this.applyColorModification(g, this.activeTab);
  }

  /**
   * Applies color modifications to a specific SVG element.
   *
   * This method iterates over the color rules defined for the current active tab.
   * For each rule, if the element's ID contains the rule's target substring and the
   * specified condition evaluates to true, it applies the corresponding color to the
   * element's 'stroke' and/or 'fill' attributes.
   *
   * @param element - The SVG element (e.g., rect, path, circle, g) to modify.
   * @param activeTab
   */
  private applyColorModification(element: SVGGraphicsElement, activeTab: string) {
    const rules = this.colorRules?.[activeTab];
    if (!rules) return;

    for (const rule of rules) {
      // Skip if the element's ID does not contain the target substring.
      if (!element.id.includes(rule.target)) continue;

      const index = this.getIndex(element); // Find the index
      if (index === null) continue; // Skip this element if no valid index was found

      // Update color if the rule's condition is met.
      if (this.evaluateCondition(rule.condition, index)) {

        if (rule.color === Color.Transparent) {
          element.setAttribute('opacity', '0');
          return;
        }

        // If the element has a 'stroke' attribute, update its color.
        if (element.hasAttribute('stroke')) {
          element.setAttribute('stroke', rule.color);
        }

        // If the element has a 'fill' attribute, update its color.
        if (element.hasAttribute('fill')) {
          element.setAttribute('fill', rule.color);
        }
      }
    }
  }

  /**
   * Configures icon elements within the SVG container using the defined icon rules.
   *
   * This method retrieves all rules under the activeTab category whose target strings contain
   * "icon". It then selects all elements with the 'icon-pointer' class from the SVG container and
   * applies the matching icon rule to each eligible element.
   */
  private setupIcon(activeTab: string): void {
    if (!this.isModificationEnabled) return;

    const iconRules = this.colorRules?.[activeTab]?.filter((rule: any) => rule.target.includes(this.ICON_TARGET));
    if (!iconRules) return; // Exit early if no icon rules are defined

    // Retrieve all SVG elements designated as icons
    const iconElements = this.svgContainer.querySelectorAll('.icon-pointer');
    iconElements.forEach((element) => {
      if (!(element instanceof SVGGraphicsElement)) return; // Skip non-SVG elements
      this.applyIconRuleToElement(iconRules, element);
    });
  }

  /**
   * Applies the first matching icon rule to a specific SVG element.
   *
   * For each provided icon rule, this method checks whether the element's ID includes the rule's target substring.
   * If so, it retrieves the corresponding index from the SVG index using the parent group identifier.
   * Should a valid index be found and the rule's condition evaluate to true, the icon is appended
   * to the element at the specified position.
   *
   * @param iconRules - An array of icon configuration rules.
   * @param element - The SVG element to process.
   */
  private applyIconRuleToElement(iconRules: any, element: SVGGraphicsElement): void {
    for (const rule of iconRules) {
      // Skip if the element's ID does not contain the target substring.
      if (!element.id.includes(rule.target)) continue;

      const index = this.getIndex(element); // Find the index
      if (index === null) return; // Skip this element if no valid index was found

      // Evaluate the rule's condition with the provided parameters.
      const isConditionMet = rule.condition(this.activeTab, index, this.svgData);
      if (!isConditionMet) continue; // Exit if the condition is not met

      // Get the element's bounding box and append the icon at the defined position and size.
      const bbox = element.getBBox();

      this.appendIconElement(element, bbox, rule.idToAdd, rule.classNameToAdd, rule.icon, rule.iconType, rule.iconSize, rule.offsetX, rule.offsetY, rule.position)
        .then(() => {
        })
        .catch(error => {
          console.error('An error occurred while loading or appending the SVG icon.');
        });

      break;
    }
  }

  /**
   * Appends an SVG icon next to a specified SVG parent element.
   *
   * @param parentElement   - The SVG element next to which the icon will be placed.
   * @param bbox            - The bounding box of the parent element (obtained via getBBox()).
   * @param id              - (Optional) The id attribute to assign to the icon.
   * @param className       - (Optional) The class attribute to assign to the icon.
   * @param iconName        - The filename of the SVG icon to load (default: 'default.svg').
   * @param iconType        - The type of the SVG icon.
   * @param iconSize        - The width and height of the icon in pixels (default: 24).
   * @param offsetX         - Horizontal offset in pixels from the parent element (default: 0).
   * @param offsetY         - Vertical offset in pixels from the parent element (default: 0).
   * @param position        - One of 'right', 'left', 'top', or 'bottom' indicating icon placement (default: 'right').
   *
   * @returns Promise<void>
   */
  private async appendIconElement(
    parentElement: Element,
    bbox: DOMRect,
    id: string = null,
    className: string = null,
    iconName: string = 'default.svg',
    iconType: string = null,
    iconSize: number = 24,
    offsetX: number = 0,
    offsetY: number = 0,
    position: Position = 'right'
  ): Promise<void> {
    try {
      // Define position calculations for each placement option
      const posMap: Record<Position, { x: number; y: number }> = {
        right: { // Place icon to the right, vertically centered
          x: bbox.x + bbox.width + offsetX,
          y: bbox.y + (bbox.height - iconSize) / 2,
        },
        left: { // Place icon to the left, vertically centered
          x: bbox.x - iconSize - offsetX,
          y: bbox.y + (bbox.height - iconSize) / 2,
        },
        top: { // Place icon above, horizontally centered
          x: bbox.x + (bbox.width - iconSize) / 2,
          y: bbox.y - iconSize - offsetY,
        },
        bottom: { // Place icon below, horizontally centered
          x: bbox.x + (bbox.width - iconSize) / 2,
          y: bbox.y + bbox.height + offsetY,
        },
      };

      // Get the x and y coordinates for the requested position, defaulting to 'right'
      const { x, y } = posMap[position] ?? posMap.right;

      let svgText: any | null = null;
      if (iconType) {
        if (this.getSvgContent(iconType) == null) {
          this.fetchSvgContent(`assets/images/${iconName}`, iconType);
        }

        svgText = this.sanitizer.bypassSecurityTrustHtml(this.getSvgContent(iconType));
      } else {
        // Load the SVG file asynchronously from assets/images/
        const response = await fetch(`assets/images/${iconName}`);
        if (!response.ok) {
          console.error(`Failed to load SVG: assets/images/${iconName}`);
          return;
        }

        // Parse the returned SVG text into a temporary container
        svgText = await response.text();
        svgText = svgText.trim();
      }

      if (svgText == null) return;

      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = svgText;
      const fetchedSvg = tempContainer.querySelector('svg');
      if (!fetchedSvg) {
        console.error(`No <svg> element found inside '${iconName}'.`);
        return;
      }

      // Clone the fetched SVG node to create an independent icon element
      const iconElem = fetchedSvg.cloneNode(true) as SVGSVGElement;
      if (id) iconElem.setAttribute('id', id);
      if (className) iconElem.setAttribute('class', className);
      iconElem.setAttribute('x', x.toString());
      iconElem.setAttribute('y', y.toString());
      iconElem.setAttribute('width', iconSize.toString());
      iconElem.setAttribute('height', iconSize.toString());
      iconElem.setAttribute('viewBox', `0 0 ${iconSize} ${iconSize}`);

      // Append the icon element after the parent element in the DOM.
      parentElement.after(iconElem);
    } catch (error) {
      console.error('An error occurred while loading or appending the SVG icon.');
    }
  }

  private getSvgContent(type): string | null {
    switch (type) {
      case CacheKeys.TRAIN_WARNING_ICON:
        return StorageUtil.getTrainWarningIcon();
      case CacheKeys.TRAIN_FIRE_ALARM_ICON:
        return StorageUtil.getTrainFireAlarmIcon();
      default:
          return null;
    }
  }

  private fetchSvgContent(url , type) {
    fetch(url)
      .then(response => response.text())
      .then(content => {
        if (type === CacheKeys.TRAIN_WARNING_ICON) {
          StorageUtil.setTrainWarningIcon(content);
        } else if (type === CacheKeys.TRAIN_FIRE_ALARM_ICON) {
          StorageUtil.setTrainFireAlarmIcon(content);
        }
      })
      .catch(error => {
        console.error('An error occurred while loading the SVG: ', error);
      });
  }

  /**
   * Sets up dynamic text elements in the SVG, adding them next to specific elements
   * based on rules defined for the current active tab.
   */
  private setupText(activeTab: string) {
    if (!this.isModificationEnabled) return;

    const textRules = this.colorRules?.[activeTab]?.filter(rule => rule.target.includes(this.TEXT_TARGET));
    if (!textRules) return;

    const textElements = Array.from(this.svgContainer.querySelectorAll('.text-pointer')) as SVGGraphicsElement[];
    textElements.forEach((element) => {
      if (!(element instanceof SVGGraphicsElement)) return; // Skip if the element is not an SVGGraphicsElement

      for (const rule of textRules) {
        if (!rule.target.includes(element.id)) continue; // Element id must be in rule.target

        const index = this.getIndex(element); // Find the index
        if (index === null) return; // Skip this element if no valid index was found

        if (!this.evaluateCondition(rule.condition, index)) return;
        const displayText = this.translateService.instant(rule.displayText);

        const bbox = element.getBBox();
        this.appendTextElement(element, bbox,
          rule.idToAdd, rule.classNameToAdd,
          rule.color, displayText, rule.fontSize, rule.fontFamily, rule.fontStyle, rule.fontWeight,
          rule.offsetX, rule.offsetY, rule.position, rule.textAnchor, rule.maxWidth, rule.lineHeight);
      }
    });
  }

  /**
   * Appends a text element beside a reference SVG element, optionally wrapping
   * into multiple lines when exceeding maxWidth, and centers multiline text.
   * If text fits within maxWidth, uses the provided textAnchor; otherwise
   * forces 'middle' anchoring for wrapped text.
   * @param id
   * @param className
   * @param parentElement   The SVG element to annotate (e.g., <path> or <rect>).
   * @param bbox            Bounding box of the parent element (getBBox()).
   * @param color           Fill color for the text.
   * @param textContent     The content string to display.
   * @param fontSize        Font size in px or as number.
   * @param fontFamily      Font family name.
   * @param fontStyle       Font style name.
   * @param fontWeight      Font weight (e.g. '400', '700').
   * @param offsetX         Horizontal offset from the reference box.
   * @param offsetY         Vertical offset from the reference box.
   * @param position        Where to place the text relative to the box.
   * @param textAnchor      'start' | 'middle' | 'end' for single-line alignment.
   * @param maxWidth        Maximum line width in pixels before wrapping.
   * @param lineHeight      Line height in px (default = fontSize * 1.2).
   */
  private appendTextElement(
    parentElement: Element,
    bbox: DOMRect,
    id: string = null,
    className: string = null,
    color: string,
    textContent: string = '-',
    fontSize: string = '16',
    fontFamily: string = 'Roboto',
    fontStyle: string = 'normal',
    fontWeight: string = '700',
    offsetX: number = 0,
    offsetY: number = 0,
    position: Position = 'right',
    textAnchor: TextAnchor = 'start',
    maxWidth: number = Infinity,
    lineHeight: number = parseFloat(fontSize) * 1.2
  ): void {

    // Compute base coordinates
    let baseX: number;
    let baseY: number;
    switch (position) {
      case 'left':
        baseX = bbox.x - offsetX;
        baseY = bbox.y + bbox.height / 2;
        break;
      case 'top':
        baseX = bbox.x + bbox.width / 2 + offsetX;
        baseY = bbox.y - offsetY;
        break;
      case 'bottom':
        baseX = bbox.x + bbox.width / 2 + offsetX;
        baseY = bbox.y + bbox.height + offsetY;
        break;
      case 'right':
      default:
        baseX = bbox.x + bbox.width + offsetX;
        baseY = bbox.y + bbox.height / 2;
        break;
    }

    // Create text element
    const textElem = document.createElementNS(this.svgNS, 'text') as SVGTextElement;
    if (id) textElem.setAttribute('id', id);
    if (className) textElem.setAttribute('class', className);
    textElem.setAttribute('fill', color);
    textElem.setAttribute('font-size', `${fontSize}px`);
    textElem.setAttribute('font-family', fontFamily);
    textElem.setAttribute('font-style', fontStyle);
    textElem.setAttribute('font-weight', fontWeight);
    textElem.setAttribute('dominant-baseline', 'middle');

    // Insert and build lines
    const parentNode = parentElement.parentNode;
    if (parentNode) parentNode.insertBefore(textElem, parentElement.nextSibling);

    // Initial content for measuring
    textElem.textContent = textContent;
    const measuredWidth = textElem.getComputedTextLength();
    const fitsSingle = measuredWidth <= maxWidth;

    // Decide anchoring
    const anchorUsed = fitsSingle ? textAnchor : 'middle';
    const xPos = fitsSingle
      ? this.computeAlignedX(baseX, maxWidth, textAnchor)
      : baseX + (isFinite(maxWidth) ? maxWidth / 2 : 0);

    textElem.setAttribute('text-anchor', anchorUsed);
    textElem.setAttribute('x', xPos.toString());
    textElem.setAttribute('y', baseY.toString());

    if (fitsSingle) {
      // Single-line case; done
      return;
    }

    // Multi-line wrapping
    textElem.textContent = '';
    const words = textContent.split(/\s+/);
    let line = '';
    let tspan = this.createTSpan(xPos, 0);
    textElem.appendChild(tspan);
    const tspans: SVGTSpanElement[] = [tspan];

    for (const word of words) {
      const testLine = line ? `${line} ${word}` : word;
      tspan.textContent = testLine;
      if (tspan.getComputedTextLength() > maxWidth) {
        tspan.textContent = line;
        tspan = this.createTSpan(xPos, lineHeight);
        tspan.textContent = word;
        textElem.appendChild(tspan);
        tspans.push(tspan);
        line = word;
      } else {
        line = testLine;
      }
    }

    // Center lines vertically
    const totalLines = tspans.length;
    if (totalLines > 1) {
      const totalHeight = (totalLines - 1) * lineHeight;
      tspans.forEach((ts, idx) => {
        const dy = idx === 0 ? -totalHeight / 2 : lineHeight;
        ts.setAttribute('dy', dy.toString());
      });
    }
  }

  private createTSpan(x: number, dy: number): SVGTSpanElement {
    const tspan = document.createElementNS(this.svgNS, 'tspan');
    tspan.setAttribute('x', x.toString());
    tspan.setAttribute('dy', dy.toString());
    return tspan;
  }

  private computeAlignedX(baseX: number, maxWidth: number, anchor: TextAnchor): number {
    switch (anchor) {
      case 'middle':
        return baseX + (isFinite(maxWidth) ? maxWidth / 2 : 0);
      case 'end':
        return baseX + (isFinite(maxWidth) ? maxWidth : 0);
      default:
        return baseX;
    }
  }

  /**
   * Evaluates the provided condition to determine if the rule should be applied.
   *
   * @param condition - A function representing the condition to evaluate.
   * @param index - The index used for evaluation.
   * @returns True if the condition is met, otherwise false.
   */
  private evaluateCondition(condition: any, index: number): boolean {
    return typeof condition === 'function' ? condition(index, this.svgData) : false;
  }

  /**
   * Returns the index from svgIndex corresponding to the parent 'g' element's ID.
   * @param element - The SVG element to process.
   * @returns The corresponding index if found; otherwise, null.
   */
  private getIndex(element: SVGGraphicsElement): number | null {
    const parentId = this.findParentGroupId(element);
    if (!parentId) return null;

    if (parentId === 'no-parent') return 0;

    // Find a key in svgIndex that is included in parentId.
    const key = this.sortedSvgIndex.find(k => parentId.includes(k));
    return key != null ? this.svgIndex[key] : null;
  }

  /**
   * Retrieves the ID of the nearest parent 'g' element for the given SVG element.
   * @param element - The SVG element to inspect.
   * @returns The ID of the parent 'g' element if found, otherwise an empty string.
   */
  private findParentGroupId(element: SVGGraphicsElement): string {
    const parentG = element.parentElement?.closest('g');
    return parentG?.id || '';
  }

  /**
   * Handles clicks on rect elements by executing the relevant click handler.
   * @param rect - The clicked SVG rect element.
   * @returns The result of the click handler.
   */
  private handleRectClick(rect: SVGRectElement): any {
    return this.handleClick(rect);
  }

  /**
   * Handles clicks on path elements by executing the relevant click handler.
   * @param path - The clicked SVG path element.
   * @returns The result of the click handler.
   */
  private handlePathClick(path: SVGPathElement): any {
    return this.handleClick(path);
  }

  /**
   * Handles clicks on circle elements by executing the relevant click handler.
   * @param circle - The clicked SVG circle element.
   * @returns The result of the click handler.
   */
  private handleCircleClick(circle: SVGCircleElement): any {
    return this.handleClick(circle);
  }

  /**
   * Handles clicks on group ('g') elements by executing the relevant click handler.
   * @param g - The clicked SVG group element.
   * @returns The result of the click handler.
   */
  private handleGroupClick(g: SVGGElement): any {
    return this.handleClick(g);
  }

  /**
   * Executes the click handler for a given SVG element.
   * @param element - The SVG element that was clicked.
   * @returns The result of the click handler or an empty string if no handler exists.
   */
  private handleClick(element: SVGGraphicsElement): any {
    const handler = this.clickHandlers[this.activeTab];

    if (handler) {
      const result = handler(element.id);

      return {
        ...result,
        index: this.getIndex(element)
      };
    }

    return '';
  }

  // Sets up the main system unit
  private setUpMainSystemUnit() {
    if (!this.isModificationEnabled) return;

    this.setupText(this.MAIN_SYSTEM);
    this.setupIcon(this.MAIN_SYSTEM);

    // Select all elements with the 'main-unit' class within the SVG container.
    const elements = this.svgContainer.querySelectorAll('.main-unit');
    elements.forEach((element) => {
      if (!(element instanceof SVGGraphicsElement)) return; // Ensure the element is an SVGGraphicsElement
      this.applyColorModification(element, this.MAIN_SYSTEM);
    });
  }
}
